import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  concat,
  concatMap,
  defer,
  EMPTY,
  map,
  Observable,
  of,
  OperatorFunction,
  switchMap,
  tap,
} from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';

import { Option, Result } from '@sapphire/result';
import { match } from 'ts-pattern';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';
import { APP_ROUTES } from '@ps/core-config';
import { ToastService } from '@ps/core-overlay';
import {
  AdvancedFeasibilityFilters,
  MC_GROUPED_MESSAGES,
  classifySurvey,
  MC_MESSAGES,
  SURVEY_CLASSIFICATION_KIND,
  Qualification,
  StandardSurveyPayloadInput,
  Survey,
  SurveyKind,
  SurveyMetadataForm,
  SurveyMarginFormState,
  SurveyPricingSnapshot,
} from '@ps/survey-utils';

import {
  CachedEntry,
  resolveQualifications,
  SurveyAudienceForm,
  SurveyLaunchSubmissionForm,
} from '../../utils';

import {
  selectInsightsChildren,
  SurveyCreationInsightsActions,
} from '../insights';

import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
} from './survey-creation-core.actions';
import { SurveyCreationLaunchApiActions } from '../launch/survey-creation-launch.actions';
import {
  SurveyCreationAudienceApiActions,
  SurveyCreationMcApiActions,
} from '../audience/survey-creation-audience.actions';
import {
  actingUser,
  gatherSurveyFormContext,
  isSaveable,
  reportFormErrors,
  saveGateForms,
} from './utils/survey-save-pipeline.helper';
import { marginSnapshot } from '../audience/domain/margin-snapshot';
import {
  SurveyPersistenceService,
  SurveyUpdateRequest,
} from '../../services/save/survey-persistence.service';
import {
  buildProjectUpdate,
  ProjectUpdateInput,
} from '../audience/utils/project-save.helper';
import { selectAudienceChildren } from '../audience/survey-creation-audience.selectors';
import {
  selectCoreChildren,
  selectRootSurveyId,
  selectSurvey,
} from './survey-creation-core.selectors';
import { SurveyChildEntry } from '../survey-creation-child-registry.helper';
import { DEFAULT_CORE_CHILD_STATE } from '../survey-creation-state.constants';
import { Nullable } from '@ps/utils';

// Same shape once the save gate has confirmed the id — and the form's data with it, so no
// stage downstream has to re-assert that a validated form really does carry one.
interface ValidatedSurveyForSave {
  readonly surveyId: number;
  readonly data: NonNullable<SurveyAudienceForm['data']>;
  readonly launch: NonNullable<SurveyLaunchSubmissionForm['data']>;
  /** Only ever filled behind the `enableTargetMargin` service-operator gate. */
  readonly marginForm: SurveyMarginFormState;
  readonly pricing: SurveyPricingSnapshot;
  readonly advancedFeasibilityFilters: AdvancedFeasibilityFilters;
  readonly qualificationsRecord: Record<string, CachedEntry<Qualification[]>>;
  /** True when the buyer did not ask for this save, so it writes without announcing. */
  readonly silent: boolean;
}

// Returns trigger actions only — a follow-up's own HTTP runs in its own effect,
// so its failures map to its own failure action, not a save failure.
type SurveySaveFollowUp = (
  survey: Survey,
  silent: boolean,
) => Observable<Action>;

// Only `saveSurvey` carries the flag; every other save-* action is a buyer
// asking for one, so it reports.
function isSilentSave(trigger: Action): boolean {
  return 'silent' in trigger && trigger.silent === true;
}

// Owns the save/clone lifecycle for a standard survey — save is dispatched
// from create, edit and launch alike, so it lives in core/ (unlike creation,
// which is create-only and lives in SurveyAudienceCreateEffects in audience/;
// both share the stage-0 read via survey-save-pipeline.helper.ts).
@Injectable()
export class SurveyCreationSaveEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _toastService = inject(ToastService);
  private readonly _router = inject(Router);

  private readonly _persistence = inject(SurveyPersistenceService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);

  // Only a plain save stays on the page, so only it refreshes insights — and
  // only when the caller asked for it. The launch page never displays
  // feasibility/score/supplier data, so it saves with recalculationsEnabled:
  // false rather than pay for a refresh nothing reads.
  public readonly saveStandardSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveSurvey),
      this._saveSurveyPipelineWithTrigger(
        (action) => () =>
          !action.recalculationsEnabled
            ? EMPTY
            : of(SurveyCreationInsightsActions.refreshAfterSave()),
      ),
    ),
  );

  public readonly saveAndCreateNewSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndCreateNew),
      this._saveSurveyPipeline(() =>
        this._navigateTo(APP_ROUTES.CREATE_SURVEY),
      ),
    ),
  );

  public readonly saveSurveyAndNavigateToLaunchPage$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndLaunch),
      this._saveSurveyPipeline((survey) =>
        this._navigateTo(
          APP_ROUTES.launchSurvey(this._routableSurveyId(survey)),
        ),
      ),
    ),
  );

  public readonly saveAndEditAudienceSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndEditAudience),
      this._saveSurveyPipeline((survey) =>
        this._navigateTo(APP_ROUTES.editSurvey(this._routableSurveyId(survey))),
      ),
    ),
  );

  // Redirect trigger is scoped to this action alone, not bare saveSurveySuccess —
  // every other save variant shares that action, so wiring off it directly would
  // make the ordinary page Save button also pop a test-link tab.
  public readonly saveAndTestSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndTest),
      this._saveSurveyPipeline(() =>
        of(SurveyCreationLaunchApiActions.testEntryLinkReady()),
      ),
    ),
  );

  public readonly saveAndCloneSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndCloneSurvey),
      this._saveSurveyPipeline((survey) => this._cloneSurvey(survey)),
    ),
  );

  // Only announces the save succeeded — SurveyCreationWorkflowExitEffects owns
  // the navigate, same split as the impersonation-exit variants below.
  public readonly saveAndExitSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndExit),
      this._saveSurveyPipeline((survey) =>
        of(SurveyCreationCoreApiActions.saveSurveyAndExitSuccess({ survey })),
      ),
    ),
  );

  // Only announces the save succeeded — SurveyCreationWorkflowExitEffects owns
  // what happens next (disconnecting, navigating). Keeps this class's one job
  // "run the save pipeline" regardless of why a caller wanted the exit.
  public readonly saveSurveyAndDisconnectImpersonation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveSurveyAndDisconnectImpersonation),
      this._saveSurveyPipeline((survey) =>
        of(
          SurveyCreationCoreApiActions.saveSurveyAndDisconnectImpersonationSuccess(
            {
              survey,
            },
          ),
        ),
      ),
    ),
  );

  // Same reasoning as above; the target buyer isn't carried through the
  // pipeline's own stages, so it's closed over per-emission here instead.
  public readonly saveSurveyAndSwitchImpersonatedBuyer$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveSurveyAndSwitchImpersonatedBuyer),
      this._saveSurveyPipelineWithTrigger(
        (action) => (survey) =>
          of(
            SurveyCreationCoreApiActions.saveSurveyAndSwitchImpersonatedBuyerSuccess(
              { survey, buyer: action.buyer },
            ),
          ),
      ),
    ),
  );

  // Same reasoning as above; routeKey/params aren't carried through the
  // pipeline's own stages, so they're closed over per-emission here instead.
  // Only announces the save succeeded — SurveyCreationWorkflowExitEffects owns
  // performing the actual switch, same split as the other save-and-X variants.
  public readonly saveSurveyAndSwitchToLegacy$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveSurveyAndSwitchToLegacy),
      concatMap(({ routeKey, params }) =>
        of<Action>(
          SurveyCreationCoreActions.saveSurveyAndSwitchToLegacy({
            routeKey,
            params,
          }),
        ).pipe(
          this._saveSurveyPipeline((survey) =>
            of(
              SurveyCreationCoreApiActions.saveSurveyAndSwitchToLegacySuccess({
                survey,
                routeKey,
                params,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // Reset before navigating so the edit page loads fresh data, not stale
  // state. concat + defer guarantees that order — tap-then-map would only
  // work by accident of Router deferring navigation to a microtask.
  public readonly navigateToEditSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationAudienceApiActions.createStandardSurveySuccess,
        SurveyCreationCoreApiActions.cloneSurveySuccess,
        SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess,
        SurveyCreationMcApiActions.createMcTraditionalSurveysSuccess,
        SurveyCreationMcApiActions.createMcGroupedProjectSuccess,
      ),
      switchMap(({ survey }) =>
        concat(
          of(SurveyCreationCoreActions.reset()),
          defer(() => this._navigateTo(APP_ROUTES.editSurvey(survey.id))),
        ),
      ),
    ),
  );

  // For a save variant whose followUp needs a value carried on the
  // triggering action itself (not produced by the pipeline's own stages,
  // e.g. recalculationsEnabled or the target buyer) — re-emits the same
  // action the pipeline validates against, so followUpFor can close over it
  // per emission instead of the pipeline threading it through.
  private _saveSurveyPipelineWithTrigger<TriggerAction extends Action>(
    followUpFor: (action: TriggerAction) => SurveySaveFollowUp,
  ): OperatorFunction<TriggerAction, Action> {
    return (trigger$) =>
      trigger$.pipe(
        concatMap((action) =>
          of<Action>(action).pipe(
            this._saveSurveyPipeline(followUpFor(action)),
          ),
        ),
      );
  }

  // Runs validate → build → persist for every save-* action. followUp must
  // return trigger actions only — its own HTTP stays in its own effect.
  private _saveSurveyPipeline(
    followUp: SurveySaveFollowUp,
  ): OperatorFunction<Action, Action> {
    return (trigger$) =>
      trigger$.pipe(
        this._validateSurveyForSave(),
        this._persistSurveyUpdate(followUp),
      );
  }

  // Stage 1 — gates on an existing, submittable survey, reporting errors if not.
  private _validateSurveyForSave(): OperatorFunction<
    Action,
    ValidatedSurveyForSave
  > {
    return (trigger$) =>
      trigger$.pipe(
        gatherSurveyFormContext(this._store),
        tap(({ audienceForm, launchForm, marginForm }) =>
          reportFormErrors(
            this._toastService,
            saveGateForms(audienceForm, launchForm, marginForm),
          ),
        ),
        // One pipeline instead of a filter paired with throwing unwraps: the id and the form's
        // data are only produced where both are known to exist, so the gate and the narrowing
        // cannot drift apart.
        concatMap(
          ({
            trigger,
            surveyId,
            audienceForm,
            launchForm,
            marginForm,
            pricing,
            advancedFeasibilityFilters,
            qualificationsRecord,
          }) =>
            Option.from(surveyId)
              .filter(() => isSaveable(audienceForm, launchForm, marginForm))
              .andThen((validatedSurveyId) =>
                Option.from(audienceForm.data).map((data) => ({
                  surveyId: validatedSurveyId,
                  silent: isSilentSave(trigger),
                  data,
                  launch: launchForm.data,
                  marginForm,
                  pricing,
                  advancedFeasibilityFilters,
                  qualificationsRecord,
                })),
              )
              .match({
                some: (validated) => of(validated),
                none: () => EMPTY,
              }),
        ),
      );
  }

  // Picks the endpoint the survey's kind belongs to. A project writes every
  // child at once and addresses the project row; everything else writes the
  // single active survey.
  private _updateRequest(
    validated: ValidatedSurveyForSave,
    survey: Nullable<Survey>,
  ): Observable<Result<SurveyUpdateRequest, string>> {
    return survey && SurveyKind.isProject(survey)
      ? this._projectUpdateRequest(validated, survey)
      : of(Result.ok(this._surveyUpdateRequest(validated)));
  }

  // The project branch, shared by every project type: resolve the project row
  // and its child registry, then let the type build its own request.
  private _projectUpdateRequest(
    validated: ValidatedSurveyForSave,
    survey: Survey,
  ): Observable<Result<SurveyUpdateRequest, string>> {
    const user = actingUser(this._authSelectors);

    return of(validated).pipe(
      concatLatestFrom(() => [
        this._store.select(selectRootSurveyId),
        this._store.select(selectAudienceChildren),
        this._store.select(selectCoreChildren),
        this._store.select(selectInsightsChildren),
      ]),
      map(
        ([
          { data, launch, pricing, qualificationsRecord },
          rootSurveyId,
          audienceChildren,
          coreChildren,
          insightsChildren,
        ]) =>
          Option.from(rootSurveyId)
            .andThen((projectId) =>
              Option.from(coreChildren[projectId]?.survey),
            )
            .map((project) =>
              this._projectRequestFor(survey, {
                project,
                activeMetadata: data.metadata,
                children: Object.entries(audienceChildren).map(
                  ([key, child]): SurveyChildEntry => [
                    key,
                    child,
                    coreChildren[key] ?? DEFAULT_CORE_CHILD_STATE,
                  ],
                ),
                launch,
                pricing,
                insightsChildren,
                qualificationsRecord,
                userId: user.id,
                companyId: user.companyId,
                enableRespondentStatusFilter:
                  !!this._userSettingsSelectors.buyerSettings()
                    .enableRespondentStatusFilter,
              }),
            )
            .unwrapOr(Result.err(MC_MESSAGES.LOCALE_NOT_SAVEABLE)),
      ),
    );
  }

  // The request's `kind` and its `input` are picked together — the discriminant
  // is what tells SurveyPersistenceService which endpoint the input belongs to,
  // so each project type owns both. Tracker and grouped have no write yet, and
  // `_skipsWrite` keeps them out of this branch until theirs lands.
  private _projectRequestFor(
    survey: Survey,
    context: Omit<ProjectUpdateInput, 'messages'>,
  ): Result<SurveyUpdateRequest, string> {
    return match(classifySurvey(survey).kind)
      .with(SURVEY_CLASSIFICATION_KIND.GROUP_MC, (kind) =>
        buildProjectUpdate({
          ...context,
          messages: {
            noChildrenSelected: MC_MESSAGES.NO_LOCALES_SELECTED,
            childNotSaveable: MC_MESSAGES.LOCALE_NOT_SAVEABLE,
          },
        }).map(
          (input): SurveyUpdateRequest => ({
            kind,
            projectId: context.project.id,
            input,
          }),
        ),
      )
      .otherwise(
        (): Result<SurveyUpdateRequest, string> =>
          Result.err(MC_MESSAGES.LOCALE_NOT_SAVEABLE),
      );
  }

  // Stage 2, standard branch — the exact input updateStandardSurvey expects.
  private _surveyUpdateRequest(
    validated: ValidatedSurveyForSave,
  ): SurveyUpdateRequest {
    return {
      kind: SURVEY_CLASSIFICATION_KIND.STANDARD,
      surveyId: validated.surveyId,
      input: this._standardSurveyInput(validated),
    };
  }

  private _standardSurveyInput({
    data,
    launch,
    marginForm,
    pricing,
    advancedFeasibilityFilters,
    qualificationsRecord,
  }: ValidatedSurveyForSave): StandardSurveyPayloadInput {
    const user = actingUser(this._authSelectors);
    const { metadata, qualifications, quotas } = data;

    return {
      metadata,
      pricing,
      userId: user.id,
      companyId: user.companyId,
      targeting: {
        qualifications,
        quotas,
        catalogQualifications: this._catalogQualifications(
          metadata,
          qualificationsRecord,
        ),
      },
      advancedFeasibilityFilters,
      enableRespondentStatusFilter:
        !!this._userSettingsSelectors.buyerSettings()
          .enableRespondentStatusFilter,
      launch,
      ...marginSnapshot(marginForm).mapOr({}, (snapshot) => ({
        margin: snapshot,
      })),
    };
  }

  // Stage 2 — builds the request the survey's kind needs, persists it, reports
  // the outcome, then chains the caller's follow-up. Building and writing share
  // the same `survey`, so they read it once.
  private _persistSurveyUpdate(
    followUp: SurveySaveFollowUp,
  ): OperatorFunction<ValidatedSurveyForSave, Action> {
    return (context$) =>
      context$.pipe(
        concatLatestFrom(() => this._store.select(selectSurvey)),
        switchMap(([validated, survey]) =>
          this._skipsWrite(survey)
            ? this._announceUnwrittenSave(survey, followUp, validated.silent)
            : this._updateRequest(validated, survey).pipe(
                switchMap((request) =>
                  request.match({
                    ok: (built) =>
                      this._writeSurveyUpdate(
                        built,
                        followUp,
                        validated.silent,
                        survey,
                      ),
                    err: (error) => this._rejectUnbuildableSave(error),
                  }),
                ),
              ),
        ),
      );
  }

  // Tracker and grouped projects still have no write of their own — their save
  // flow is being sorted out separately. A Group MC is no longer among them: it
  // persists through the project endpoint.
  private _skipsWrite(survey: Nullable<Survey>): survey is Survey {
    return (
      !!survey && SurveyKind.isProject(survey) && !SurveyKind.isGroupMc(survey)
    );
  }

  // Nothing was written, so there is no fresh survey and no save toast — this
  // only announces success so the caller's follow-up still runs.
  private _announceUnwrittenSave(
    survey: Survey,
    followUp: SurveySaveFollowUp,
    silent: boolean,
  ): Observable<Action> {
    return concat(
      of(SurveyCreationCoreApiActions.saveSurveySuccess({ survey })),
      followUp(survey, silent),
    );
  }

  private _rejectUnbuildableSave(error: string): Observable<Action> {
    this._toastService.error(error, { keepOpen: false });
    return of(SurveyCreationCoreApiActions.saveSurveyFailure({ error }));
  }

  private _writeSurveyUpdate(
    request: SurveyUpdateRequest,
    followUp: SurveySaveFollowUp,
    silent: boolean,
    survey: Nullable<Survey>,
  ): Observable<Action> {
    return this._persistence.update(request).pipe(
      tap(() => {
        if (!silent) {
          this._toastService.success('Survey successfully saved', {
            keepOpen: false,
          });
        }
      }),
      switchMap((savedSurvey) =>
        concat(
          of(
            SurveyCreationCoreApiActions.saveSurveySuccess({
              survey: savedSurvey,
            }),
          ),
          followUp(savedSurvey, silent),
        ),
      ),
      this.psCatchError(
        (error) => {
          const message = this._saveErrorMessage(error.message, survey);
          this._toastService.error(message);
          return of(
            SurveyCreationCoreApiActions.saveSurveyFailure({
              error: message,
            }),
          );
        },
        { notify: false },
      ),
    );
  }

  private _saveErrorMessage(message: string, survey: Nullable<Survey>): string {
    if (!survey || !SurveyKind.isTraditionalMc(survey)) {
      return message;
    }

    return `${message} to ${survey.countryName}-${survey.languageName} Survey`;
  }

  // Emits cloneSurveySuccess/Failure so navigateToEditSurvey$ (and any other listener) reacts
  // the same way regardless of the caller.
  //
  // A Group MC has already been persisted by the time this runs — only the
  // clone itself is unsupported, so it reports that and contributes no action
  // rather than failing the save the buyer just completed.
  private _cloneSurvey(survey: Survey): Observable<Action> {
    if (SurveyKind.isGroupMc(survey)) {
      this._toastService.info(MC_GROUPED_MESSAGES.CLONE_UNAVAILABLE);
      return EMPTY;
    }

    return this._persistence
      .clone(survey.id, this._authSelectors.hasImpersonatedBuyer())
      .pipe(
        map((clonedSurvey) => {
          this._toastService.success('Survey successfully cloned', {
            keepOpen: false,
          });
          return SurveyCreationCoreApiActions.cloneSurveySuccess({
            survey: clonedSurvey,
          });
        }),
        this.psCatchError(
          (error) =>
            of(
              SurveyCreationCoreApiActions.cloneSurveyFailure({
                error: error.message,
              }),
            ),
          { notify: true },
        ),
      );
  }

  // Navigates as a side effect and contributes no action to the stream.
  private _navigateTo(route: string): Observable<never> {
    this._router.navigate([route]);
    return EMPTY;
  }

  // A project-backed survey is routed by its project id, not the child
  // survey's own id.
  private _routableSurveyId(survey: Survey): number {
    return SurveyKind.isProject(survey)
      ? Option.from(survey.projectId).unwrapOr(survey.id)
      : survey.id;
  }

  // The unfiltered catalog (232 included) — the survey's own qualifications never carry a 232
  // entry (see StandardSurveyTargetingInput.catalogQualifications), so this is read directly off
  // the reference-data selector, the same source the audience qualifications section reads before
  // it strips 232 via prepareVisibleQualifications for display.
  private _catalogQualifications(
    metadata: SurveyMetadataForm,
    qualificationsRecord: Record<string, CachedEntry<Qualification[]>>,
  ): Qualification[] {
    return resolveQualifications(
      qualificationsRecord,
      {
        countryCode: metadata.selectedCountries[0] ?? '',
        languageShortCode: metadata.selectedLanguages[0] ?? '',
      },
      true,
    );
  }
}
